
import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CanvasElement } from './CanvasMetadataManager';

interface InfiniteCanvasProps {
  elements?: CanvasElement[];
  onElementAdd?: (element: Omit<CanvasElement, 'id'>) => void;
  onElementUpdate?: (id: string, updates: Partial<CanvasElement>) => void;
  onElementDelete?: (id: string) => void;
  onElementConnect?: (fromId: string, toId: string) => void;
  onElementRightClick?: (element: CanvasElement, x: number, y: number) => void;
  onCanvasRightClick?: (x: number, y: number) => void;
  onElementsSelect?: (elementIds: string[]) => void;
  onElementEditStart?: (element: CanvasElement) => void;
  onStoryBranchVisualize?: (storyContext: any) => void;
  isReadOnly?: boolean;
  canvasMetadata?: {
    worldview?: string;
    backgroundStory?: string;
    theme?: string;
    timeperiod?: string;
    [key: string]: any;
  };
}

const InfiniteCanvas: React.FC<InfiniteCanvasProps> = ({
  elements = [],
  onElementAdd,
  onElementUpdate,
  onElementDelete,
  onElementConnect,
  onElementRightClick,
  onCanvasRightClick,
  onElementsSelect,
  onElementEditStart,
  onStoryBranchVisualize,
  isReadOnly = false,
  canvasMetadata = {}
}) => {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [viewOffset, setViewOffset] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [selectedElements, setSelectedElements] = useState<string[]>([]);
  const [draggedElement, setDraggedElement] = useState<string | null>(null);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPos, setContextMenuPos] = useState({ x: 0, y: 0 });
  const [contextMenuWorldPos, setContextMenuWorldPos] = useState({ x: 0, y: 0 });
  const [contextMenuElement, setContextMenuElement] = useState<CanvasElement | null>(null);
  const [selectionBox, setSelectionBox] = useState<{ start: { x: number; y: number }; end: { x: number; y: number } } | null>(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState<'se' | 'sw' | 'ne' | 'nw' | null>(null);
  const [resizeStartSize, setResizeStartSize] = useState({ width: 0, height: 0 });
  const [animationFrame, setAnimationFrame] = useState(0);
  const [imageCache, setImageCache] = useState<{ [src: string]: HTMLImageElement }>({});
  const [lastClickTime, setLastClickTime] = useState(0);
  const [lastClickElement, setLastClickElement] = useState<string | null>(null);

  const screenToWorld = useCallback((screenX: number, screenY: number) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return {
      x: (screenX - rect.left - viewOffset.x) / scale,
      y: (screenY - rect.top - viewOffset.y) / scale
    };
  }, [viewOffset, scale]);

  const getElementAt = useCallback((worldX: number, worldY: number): CanvasElement | null => {
    for (let i = elements.length - 1; i >= 0; i--) {
      const element = elements[i];
      if (worldX >= element.x && worldX <= element.x + element.width && worldY >= element.y && worldY <= element.y + element.height) {
        return element;
      }
    }
    return null;
  }, [elements]);

  const getResizeHandle = useCallback((element: CanvasElement, worldX: number, worldY: number): 'se' | 'sw' | 'ne' | 'nw' | null => {
    const tolerance = 10 / scale;
    if (Math.abs(worldX - (element.x + element.width)) < tolerance && Math.abs(worldY - (element.y + element.height)) < tolerance) return 'se';
    if (Math.abs(worldX - element.x) < tolerance && Math.abs(worldY - (element.y + element.height)) < tolerance) return 'sw';
    if (Math.abs(worldX - (element.x + element.width)) < tolerance && Math.abs(worldY - element.y) < tolerance) return 'ne';
    if (Math.abs(worldX - element.x) < tolerance && Math.abs(worldY - element.y) < tolerance) return 'nw';
    return null;
  }, [scale]);

  const drawResizeHandles = useCallback((ctx: CanvasRenderingContext2D, element: CanvasElement) => {
    const handleRadius = 6 / scale;
    const handles = [
      { x: element.x, y: element.y, cursor: 'nwse-resize' },
      { x: element.x + element.width, y: element.y, cursor: 'nesw-resize' },
      { x: element.x, y: element.y + element.height, cursor: 'nesw-resize' },
      { x: element.x + element.width, y: element.y + element.height, cursor: 'nwse-resize' }
    ];

    ctx.fillStyle = '#4361ee';
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2 / scale;

    handles.forEach(handle => {
      ctx.beginPath();
      ctx.arc(handle.x, handle.y, handleRadius, 0, 2 * Math.PI);
      ctx.fill();
      ctx.stroke();
    });
  }, [scale]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const drawChatBox = useCallback((ctx: CanvasRenderingContext2D, element: CanvasElement) => {
    const isSelected = selectedElements.includes(element.id);
    const isLoading = element.attributes?.isLoading;

    if (isSelected) {
      ctx.strokeStyle = '#4361ee';
      ctx.lineWidth = 4 / scale;
      ctx.strokeRect(element.x - 4 / scale, element.y - 4 / scale, element.width + 8 / scale, element.height + 8 / scale);
    }

    // Main container with improved styling
    const containerOpacity = isLoading ? 0.8 : 0.95;
    ctx.fillStyle = `rgba(22, 33, 62, ${containerOpacity})`;

    // Add pulsing effect for loading state
    if (isLoading) {
      const pulse = Math.sin(Date.now() * 0.005) * 0.2 + 0.8;
      ctx.strokeStyle = `rgba(76, 201, 240, ${pulse})`;
      ctx.lineWidth = 2 / scale;
    } else {
      ctx.strokeStyle = 'rgba(76, 201, 240, 0.6)';
      ctx.lineWidth = 1.5 / scale;
    }

    const radius = 16 / scale;
    ctx.beginPath();
    ctx.roundRect(element.x, element.y, element.width, element.height, radius);
    ctx.fill();
    ctx.stroke();

    // Header with gradient
    const headerHeight = 40 / scale;
    const gradient = ctx.createLinearGradient(element.x, element.y, element.x, element.y + headerHeight);

    if (isLoading) {
      // Animated gradient for loading state
      const phase = (Date.now() * 0.003) % 1;
      gradient.addColorStop(0, `rgba(67, 97, 238, ${0.3 + phase * 0.2})`);
      gradient.addColorStop(1, `rgba(76, 201, 240, ${0.2 + phase * 0.15})`);
    } else {
      gradient.addColorStop(0, 'rgba(67, 97, 238, 0.5)');
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0.3)');
    }

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.roundRect(element.x, element.y, element.width, headerHeight, [radius, radius, 0, 0]);
    ctx.fill();

    // Title with better spacing
    ctx.fillStyle = '#ffffff';
    ctx.font = `bold ${15 / scale}px "Segoe UI", Arial, sans-serif`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    const titleText = element.title.length > 30 ? element.title.substring(0, 27) + '...' : element.title;
    ctx.fillText(titleText, element.x + 16 / scale, element.y + headerHeight / 2);

    // Loading indicator in header
    if (isLoading) {
      const dots = Math.floor((Date.now() * 0.005) % 4);
      const loadingText = '●'.repeat(dots + 1) + '○'.repeat(3 - dots);
      ctx.font = `${12 / scale}px "Segoe UI", Arial, sans-serif`;
      ctx.textAlign = 'right';
      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.fillText(loadingText, element.x + element.width - 16 / scale, element.y + headerHeight / 2);
    }

    // Content area with proper padding
    const contentStartY = element.y + headerHeight + 8 / scale;
    const paddingX = 16 / scale;
    const paddingY = 8 / scale;
    const maxWidth = element.width - paddingX * 2;
    const maxHeight = element.height - headerHeight - paddingY * 2;
    let currentY = contentStartY;

    // Character color mapping for consistent bubble colors
    const characterColors: { [key: string]: string } = {};
    const defaultColors = [
      '#4361ee', '#3a86ff', '#4cc9f0', '#4895ef', '#560bad',
      '#f72585', '#7209b7', '#3f37c9', '#4cc9f0', '#b5179e'
    ];

    // Default avatar emojis for characters without images
    const defaultAvatars = ['👨', '👩', '🧙‍♂️', '🧙‍♀️', '👸', '🤴', '🧝‍♂️', '🧝‍♀️', '🧛‍♂️', '🧛‍♀️', '🧚‍♂️', '🧚‍♀️', '👺', '👻', '🤖', '👽', '🦊', '🐱', '🐶', '🐺'];
    const characterAvatars: { [key: string]: string } = {};

    let colorIndex = 0;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    let avatarIndex = 0;

    if (element.dialogues && element.dialogues.length > 0) {
      // First pass to assign colors and avatars to characters
      element.dialogues.forEach(dialogue => {
        if (dialogue.type === 'dialogue' && dialogue.character) {
          // Assign color if not already assigned
          if (!characterColors[dialogue.character]) {
            characterColors[dialogue.character] = defaultColors[colorIndex % defaultColors.length];
            colorIndex++;
          }

          // Assign avatar if not already assigned
          if (!characterAvatars[dialogue.character]) {
            // Check if this character has an image in elements
            const characterElement = elements.find(el =>
              el.type === 'character' &&
              el.title.toLowerCase() === dialogue.character?.toLowerCase()
            );

            if (characterElement && characterElement.imageUrl) {
              characterAvatars[dialogue.character] = characterElement.imageUrl;
            } else {
              // Use a consistent random avatar based on character name
              const nameHash = dialogue.character.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
              characterAvatars[dialogue.character] = defaultAvatars[nameHash % defaultAvatars.length];
              avatarIndex++;
            }
          }
        }
      });

      // Track the last speaker to group consecutive messages
      let lastSpeaker = '';
      let lastDialogueType = '';

      for (const dialogue of element.dialogues) {
        if (currentY - contentStartY >= maxHeight - 30 / scale) break;

        // Define styles based on dialogue type
        const tagStyle = {
          narration: {
            bg: 'rgba(255, 255, 255, 0.05)',
            text: isLoading ? 'rgba(255, 255, 255, 0.6)' : 'rgba(255, 255, 255, 0.75)',
            font: `italic ${11 / scale}px "Segoe UI", Arial, sans-serif`,
            borderColor: 'transparent'
          },
          thought: {
            bg: isLoading ? 'rgba(76, 201, 240, 0.08)' : 'rgba(76, 201, 240, 0.15)',
            text: isLoading ? 'rgba(200, 235, 255, 0.7)' : 'rgba(200, 235, 255, 0.95)',
            font: `${11 / scale}px "Segoe UI", Arial, sans-serif`,
            borderColor: 'rgba(76, 201, 240, 0.3)'
          },
          dialogue: {
            bg: 'rgba(67, 97, 238, 0.18)',
            text: '#ffffff',
            font: `${11 / scale}px "Segoe UI", Arial, sans-serif`,
            borderColor: 'rgba(67, 97, 238, 0.4)'
          }
        };

        const style = tagStyle[dialogue.type];
        ctx.font = style.font;

        // Prepare text content
        let displayText = dialogue.content;
        let characterName = dialogue.character || '';

        // No special pre-processing for narration text - we'll handle it directly in the rendering

        // Add typing effect for loading state
        if (isLoading && dialogue.type === 'thought') {
          const typingPhase = Math.floor((Date.now() * 0.01) % (displayText.length + 10));
          displayText = displayText.substring(0, typingPhase) + (typingPhase < displayText.length ? '|' : '');
        }

        // Improved word wrapping for content
        const words = displayText.split(' ');
        let line = '';
        const lines = [];
        const lineHeight = 15 / scale;

        // Different padding for different dialogue types
        const bubblePadding = dialogue.type === 'dialogue' ? 12 / scale : 8 / scale;

        // Calculate available width based on dialogue type
        let availableWidth;
        if (dialogue.type === 'narration') {
          // Significantly reduce available width for narration to force more line breaks
          availableWidth = Math.min(maxWidth - 60 / scale, element.width * 0.7); // Force narration to wrap more aggressively
        } else if (dialogue.type === 'dialogue') {
          availableWidth = maxWidth - bubblePadding * 2 - (characterName ? 40 / scale : 0); // Account for avatar
        } else {
          availableWidth = maxWidth - bubblePadding * 2;
        }

        // Word wrapping
        for (let n = 0; n < words.length; n++) {
          const testLine = line + words[n] + ' ';
          const metrics = ctx.measureText(testLine);
          if (metrics.width > availableWidth && n > 0) {
            lines.push(line.trim());
            line = words[n] + ' ';
          } else {
            line = testLine;
          }
        }
        if (line.trim()) lines.push(line.trim());

        // Calculate block height
        const blockHeight = lines.length * lineHeight + bubblePadding * 2;

        // Check if block fits in remaining space
        if (currentY + blockHeight > element.y + element.height - paddingY) break;

        // Determine if this is a new speaker (for spacing)
        const isNewSpeaker = dialogue.character !== lastSpeaker || dialogue.type !== lastDialogueType;
        if (isNewSpeaker && currentY > contentStartY) {
          currentY += 8 / scale; // Add extra space between different speakers
        }

        // Update last speaker tracking
        lastSpeaker = dialogue.character || '';
        lastDialogueType = dialogue.type;

        // Different rendering based on dialogue type
        if (dialogue.type === 'narration') {
          // Completely different approach for narration text
          // We'll create a separate text box with fixed width and manually split text

          // Create a visible background with higher contrast
          ctx.fillStyle = 'rgba(0, 0, 0, 0.6)'; // Darker background for better contrast

          // Calculate dimensions for the narration box - make it narrower to fit within boundaries
          const narrationBoxWidth = Math.min(element.width * 0.75, 280 / scale);
          const narrationBoxX = element.x + (element.width - narrationBoxWidth) / 2; // Center horizontally

          // Set up text rendering properties
          ctx.font = `italic ${14 / scale}px "Segoe UI", Arial, sans-serif`;

          // Hard-code the narration text into multiple very short lines
          // This ensures the text is always displayed properly regardless of word wrapping
          const narrationText = displayText;
          const hardcodedLines = [];

          // Split the text into much shorter chunks (15-18 characters max)
          let remainingText = narrationText;
          while (remainingText.length > 0) {
            // Find a good breaking point (space) around 15-18 characters
            let breakPoint = Math.min(18, remainingText.length);

            // If we're not at the end, find the last space before the break point
            if (breakPoint < remainingText.length) {
              while (breakPoint > 0 && remainingText[breakPoint] !== ' ') {
                breakPoint--;
              }

              // If no space found, force break at a shorter length
              if (breakPoint === 0) {
                breakPoint = Math.min(15, remainingText.length);
              }
            }

            // Add this line to our hardcoded lines
            hardcodedLines.push(remainingText.substring(0, breakPoint).trim());

            // Remove the processed text
            remainingText = remainingText.substring(breakPoint).trim();
          }

          // Calculate total height needed
          const lineHeight = 22 / scale; // Increased line height for better readability
          const padding = 16 / scale;
          const totalHeight = Math.max(60 / scale, hardcodedLines.length * lineHeight + padding * 2);

          // Draw the background box
          ctx.beginPath();
          ctx.roundRect(
            narrationBoxX,
            currentY,
            narrationBoxWidth,
            totalHeight,
            8 / scale
          );
          ctx.fill();

          // Add a more visible border
          ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
          ctx.lineWidth = 1.5 / scale;
          ctx.stroke();

          // Draw narration icon
          ctx.fillStyle = 'rgba(255, 255, 255, 1.0)';
          ctx.font = `${18 / scale}px Arial`;
          ctx.textAlign = 'left';
          ctx.textBaseline = 'top';
          ctx.fillText('📜', narrationBoxX + padding, currentY + padding);

          // Draw narration text
          ctx.fillStyle = 'rgba(255, 255, 255, 1.0)'; // Pure white text for maximum contrast
          ctx.font = `italic ${14 / scale}px "Segoe UI", Arial, sans-serif`;
          ctx.textAlign = 'left';
          ctx.textBaseline = 'top';

          // Draw each hardcoded line
          const baseY = currentY;
          hardcodedLines.forEach((line, i) => {
            const textY = baseY + padding + i * lineHeight;
            ctx.fillText(line, narrationBoxX + padding + 30 / scale, textY);
          });

          // Update the current Y position
          currentY += totalHeight + 16 / scale;

        } else if (dialogue.type === 'dialogue') {
          // Dialogue - chat bubble style with avatar
          const avatarSize = 32 / scale;
          const avatarMargin = 8 / scale;
          const bubbleWidth = Math.min(
            maxWidth * 0.85 - avatarSize - avatarMargin,
            Math.max(maxWidth * 0.4, ctx.measureText(lines.reduce((a, b) => a.length > b.length ? a : b, '')).width + bubblePadding * 2)
          );
          const bubbleHeight = blockHeight;

          // Determine bubble position (left for others, right for user/player)
          const isCurrentUser = characterName.toLowerCase().includes('player') || characterName.toLowerCase().includes('user');

          // Get character color
          const charColor = characterColors[characterName] || defaultColors[0];
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const avatar = characterAvatars[characterName] || defaultAvatars[0];

          // Position bubble and avatar
          let bubbleX: number = 0;
          let avatarX: number = 0;
          if (isCurrentUser) {
            bubbleX = element.x + element.width - paddingX - bubbleWidth;
            avatarX = bubbleX + bubbleWidth + avatarMargin;
          } else {
            avatarX = element.x + paddingX;
            bubbleX = avatarX + avatarSize + avatarMargin;
          }

          // Draw avatar with character name initials
          ctx.save();
          ctx.beginPath();
          ctx.arc(
            avatarX + avatarSize / 2,
            currentY + avatarSize / 2,
            avatarSize / 2,
            0,
            Math.PI * 2
          );
          ctx.fillStyle = charColor;
          ctx.fill();
          ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
          ctx.lineWidth = 1 / scale;
          ctx.stroke();

          // Draw character initials instead of image
          ctx.fillStyle = '#ffffff';
          ctx.font = `bold ${avatarSize * 0.4}px Arial`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';

          // Get initials from character name (up to 2 characters)
          const initials = characterName
            .split(' ')
            .map(word => word.charAt(0).toUpperCase())
            .slice(0, 2)
            .join('');

          ctx.fillText(initials || '?', avatarX + avatarSize / 2, currentY + avatarSize / 2);
          ctx.restore();

          // Draw character name above bubble
          ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
          ctx.font = `bold ${10 / scale}px "Segoe UI", Arial, sans-serif`;
          ctx.textAlign = isCurrentUser ? 'right' : 'left';
          ctx.textBaseline = 'bottom';
          ctx.fillText(characterName,
            isCurrentUser ? bubbleX + bubbleWidth - bubblePadding : bubbleX + bubblePadding,
            currentY - 2 / scale);

          // Draw chat bubble
          const bubbleRadius = 10 / scale;
          ctx.fillStyle = isCurrentUser
            ? `rgba(67, 97, 238, 0.25)`
            : `rgba(${parseInt(charColor.slice(1, 3), 16)}, ${parseInt(charColor.slice(3, 5), 16)}, ${parseInt(charColor.slice(5, 7), 16)}, 0.25)`;

          // Create bubble path with tail
          ctx.beginPath();
          if (isCurrentUser) {
            // Right-aligned bubble with tail on right
            ctx.roundRect(bubbleX, currentY, bubbleWidth, bubbleHeight, bubbleRadius);
            // Add tail
            ctx.moveTo(bubbleX + bubbleWidth, currentY + bubbleHeight * 0.3);
            ctx.lineTo(bubbleX + bubbleWidth + 8 / scale, currentY + bubbleHeight * 0.5);
            ctx.lineTo(bubbleX + bubbleWidth, currentY + bubbleHeight * 0.7);
          } else {
            // Left-aligned bubble with tail on left
            ctx.roundRect(bubbleX, currentY, bubbleWidth, bubbleHeight, bubbleRadius);
            // Add tail
            ctx.moveTo(bubbleX, currentY + bubbleHeight * 0.3);
            ctx.lineTo(bubbleX - 8 / scale, currentY + bubbleHeight * 0.5);
            ctx.lineTo(bubbleX, currentY + bubbleHeight * 0.7);
          }
          ctx.closePath();
          ctx.fill();

          // Add subtle border to bubble
          ctx.strokeStyle = isCurrentUser
            ? 'rgba(67, 97, 238, 0.4)'
            : `rgba(${parseInt(charColor.slice(1, 3), 16)}, ${parseInt(charColor.slice(3, 5), 16)}, ${parseInt(charColor.slice(5, 7), 16)}, 0.4)`;
          ctx.lineWidth = 1 / scale;
          ctx.stroke();

          // Draw text inside bubble
          ctx.fillStyle = '#ffffff';
          ctx.font = style.font;
          ctx.textAlign = 'left';
          ctx.textBaseline = 'top';

          const baseY = currentY;
          lines.forEach((line, i) => {
            const textY = baseY + bubblePadding + i * lineHeight;
            ctx.fillText(line, bubbleX + bubblePadding, textY, bubbleWidth - bubblePadding * 2);
          });

          currentY += bubbleHeight + 12 / scale;

        } else if (dialogue.type === 'thought') {
          // Thought - subtle bubble with thought icon
          const thoughtWidth = Math.min(maxWidth * 0.9, Math.max(maxWidth * 0.5, ctx.measureText(lines.reduce((a, b) => a.length > b.length ? a : b, '')).width + bubblePadding * 2));

          // Draw thought bubble
          ctx.fillStyle = style.bg;
          ctx.beginPath();
          ctx.roundRect(element.x + (element.width - thoughtWidth) / 2, currentY, thoughtWidth, blockHeight, 8 / scale);
          ctx.fill();

          // Add decorative border
          ctx.strokeStyle = style.borderColor;
          ctx.lineWidth = 1 / scale;
          ctx.stroke();

          // Draw thought icon
          ctx.fillStyle = style.text;
          ctx.font = `${13 / scale}px Arial`;
          ctx.textAlign = 'left';
          ctx.textBaseline = 'top';
          ctx.fillText('💭', element.x + (element.width - thoughtWidth) / 2 + bubblePadding, currentY + bubblePadding);

          // Draw text
          ctx.fillStyle = style.text;
          ctx.font = style.font;
          ctx.textBaseline = 'top';

          const iconWidth = 16 / scale;
          const thoughtBaseY = currentY;
          lines.forEach((line, i) => {
            const textY = thoughtBaseY + bubblePadding + i * lineHeight;
            ctx.fillText(line,
              element.x + (element.width - thoughtWidth) / 2 + bubblePadding + iconWidth + 4 / scale,
              textY,
              thoughtWidth - bubblePadding * 2 - iconWidth - 4 / scale);
          });

          currentY += blockHeight + 8 / scale;
        }
      }

      // Add scroll indicator if content is clipped
      if (element.dialogues.length > 0 && currentY - contentStartY >= maxHeight - 30 / scale) {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
        ctx.font = `${10 / scale}px "Segoe UI", Arial, sans-serif`;
        ctx.textAlign = 'center';
        ctx.fillText('...', element.x + element.width / 2, element.y + element.height - 12 / scale);
      }
    } else {
      // Empty state
      ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
      ctx.font = `italic ${12 / scale}px "Segoe UI", Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(isLoading ? 'AI正在创作中...' : 'No dialogue yet...', element.x + element.width / 2, element.y + element.height / 2);
    }

    if (isSelected) drawResizeHandles(ctx, element);
  }, [scale, selectedElements, drawResizeHandles, elements]);

  const drawCharacterBadge = useCallback((ctx: CanvasRenderingContext2D, element: CanvasElement) => {
    const centerX = element.x + element.width / 2;
    const centerY = element.y + element.height / 2;
    const radius = Math.min(element.width, element.height) / 2;
    const isSelected = selectedElements.includes(element.id);

    if (isSelected) {
      ctx.strokeStyle = '#4361ee';
      ctx.lineWidth = 4 / scale;
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius + 6 / scale, 0, 2 * Math.PI);
      ctx.stroke();
    }

    ctx.save();
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
    ctx.fillStyle = element.color;
    ctx.fill();
    ctx.clip();

    const img = element.imageUrl ? imageCache[element.imageUrl] : null;
    if (img && img.complete) {
      ctx.drawImage(img, element.x, element.y, element.width, element.height);
    } else {
      ctx.fillStyle = '#ffffff';
      ctx.font = `bold ${radius * 0.8}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('👤', centerX, centerY);
    }
    ctx.restore();

    ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
    ctx.lineWidth = 2 / scale;
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
    ctx.stroke();

    ctx.fillStyle = '#ffffff';
    ctx.font = `bold ${12 / scale}px "Segoe UI", Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText(element.title, centerX, centerY + radius + 8 / scale);

    if (isSelected) drawResizeHandles(ctx, element);
  }, [scale, selectedElements, drawResizeHandles, imageCache]);

  const drawSceneCard = useCallback((ctx: CanvasRenderingContext2D, element: CanvasElement) => {
    const isSelected = selectedElements.includes(element.id);
    if (isSelected) {
      ctx.strokeStyle = '#4361ee';
      ctx.lineWidth = 3 / scale;
      ctx.strokeRect(element.x - 3 / scale, element.y - 3 / scale, element.width + 6 / scale, element.height + 6 / scale);
    }

    ctx.fillStyle = element.color + '99';
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
    ctx.lineWidth = 1 / scale;
    const radius = 8 / scale;
    ctx.beginPath();
    ctx.roundRect(element.x, element.y, element.width, element.height, radius);
    ctx.fill();
    ctx.stroke();

    ctx.fillStyle = element.color;
    ctx.beginPath();
    ctx.roundRect(element.x, element.y, element.width, 30 / scale, [radius, radius, 0, 0]);
    ctx.fill();

    ctx.fillStyle = '#ffffff';
    ctx.font = `${16 / scale}px Arial`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText('🎬', element.x + 8 / scale, element.y + 15 / scale);

    ctx.font = `bold ${14 / scale}px "Segoe UI", Arial`;
    ctx.fillText(element.title, element.x + 30 / scale, element.y + 15 / scale);

    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.font = `${11 / scale}px "Segoe UI", Arial`;
    ctx.textBaseline = 'top';
    const contentY = element.y + 40 / scale;
    const baseContentY = contentY;
    element.content.split('\n').slice(0, 4).forEach((line, index) => {
      ctx.fillText(line, element.x + 8 / scale, baseContentY + (index * 14 / scale), element.width - 16 / scale);
    });

    if (isSelected) drawResizeHandles(ctx, element);
  }, [scale, selectedElements, drawResizeHandles]);

  const drawEventNode = useCallback((ctx: CanvasRenderingContext2D, element: CanvasElement) => {
    const isSelected = selectedElements.includes(element.id);
    if (isSelected) {
      ctx.strokeStyle = '#4361ee';
      ctx.lineWidth = 3 / scale;
      ctx.strokeRect(element.x - 3 / scale, element.y - 3 / scale, element.width + 6 / scale, element.height + 6 / scale);
    }

    ctx.fillStyle = element.color + '99';
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
    ctx.lineWidth = 1 / scale;
    const centerX = element.x + element.width / 2;
    const centerY = element.y + element.height / 2;

    ctx.beginPath();
    ctx.moveTo(centerX, element.y);
    ctx.lineTo(element.x + element.width, centerY);
    ctx.lineTo(centerX, element.y + element.height);
    ctx.lineTo(element.x, centerY);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    ctx.fillStyle = '#ffffff';
    ctx.font = `${16 / scale}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('⚡', centerX, centerY - 8 / scale);

    ctx.font = `bold ${10 / scale}px "Segoe UI", Arial`;
    ctx.fillText(element.title, centerX, centerY + 12 / scale);

    if (isSelected) drawResizeHandles(ctx, element);
  }, [scale, selectedElements, drawResizeHandles]);

  const drawWorldbuildingElement = useCallback((ctx: CanvasRenderingContext2D, element: CanvasElement) => {
    const isSelected = selectedElements.includes(element.id);
    if (isSelected) {
      ctx.strokeStyle = '#4361ee';
      ctx.lineWidth = 3 / scale;
      ctx.strokeRect(element.x - 3 / scale, element.y - 3 / scale, element.width + 6 / scale, element.height + 6 / scale);
    }

    ctx.fillStyle = element.color + '99';
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
    ctx.lineWidth = 1 / scale;
    const centerX = element.x + element.width / 2;
    const centerY = element.y + element.height / 2;
    const radius = Math.min(element.width, element.height) / 2;

    ctx.beginPath();
    for (let i = 0; i < 6; i++) {
      const angle = i * Math.PI / 3;
      ctx.lineTo(centerX + radius * Math.cos(angle), centerY + radius * Math.sin(angle));
    }
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    ctx.fillStyle = '#ffffff';
    ctx.font = `${16 / scale}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('🌍', centerX, centerY - 8 / scale);

    ctx.font = `bold ${10 / scale}px "Segoe UI", Arial`;
    ctx.fillText(element.title, centerX, centerY + 12 / scale);

    if (isSelected) drawResizeHandles(ctx, element);
  }, [scale, selectedElements, drawResizeHandles]);

  const getEdgeOfElement = useCallback((from: CanvasElement, to: CanvasElement) => {
    // Calculate center points
    const fromCenterX = from.x + from.width / 2;
    const fromCenterY = from.y + from.height / 2;
    const toCenterX = to.x + to.width / 2;
    const toCenterY = to.y + to.height / 2;

    // Calculate direction vector
    const dx = toCenterX - fromCenterX;
    const dy = toCenterY - fromCenterY;

    // Handle special case: centers are too close
    if (Math.abs(dx) < 0.001 && Math.abs(dy) < 0.001) {
      return { x: fromCenterX, y: fromCenterY };
    }

    // Calculate angle between elements
    const angle = Math.atan2(dy, dx);

    // Special handling for different element types
    if (from.type === 'character') {
      // For character (circular shape), calculate edge point on the circle
      const radius = Math.min(from.width, from.height) / 2;
      return {
        x: fromCenterX + Math.cos(angle) * radius,
        y: fromCenterY + Math.sin(angle) * radius
      };
    } else if (from.type === 'event') {
      // For event (diamond shape), need special calculation
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const halfW = from.width / 2;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const halfH = from.height / 2;

      // Normalize angle to 0-2π range
      let normalizedAngle = angle;
      while (normalizedAngle < 0) normalizedAngle += 2 * Math.PI;
      normalizedAngle = normalizedAngle % (2 * Math.PI);

      // Diamond has 4 corners, determine which edge to use based on angle
      if (normalizedAngle <= Math.PI / 4 || normalizedAngle > 7 * Math.PI / 4) {
        // Right point
        return { x: from.x + from.width, y: fromCenterY };
      } else if (normalizedAngle <= 3 * Math.PI / 4) {
        // Bottom point
        return { x: fromCenterX, y: from.y + from.height };
      } else if (normalizedAngle <= 5 * Math.PI / 4) {
        // Left point
        return { x: from.x, y: fromCenterY };
      } else {
        // Top point
        return { x: fromCenterX, y: from.y };
      }
    } else if (from.type === 'worldbuilding') {
      // For worldbuilding (hexagon shape), approximate with a circle
      const radius = Math.min(from.width, from.height) / 2;
      return {
        x: fromCenterX + Math.cos(angle) * radius,
        y: fromCenterY + Math.sin(angle) * radius
      };
    } else {
      // For rectangular shapes (scene, chatbox)
      const fromHalfW = from.width / 2;
      const fromHalfH = from.height / 2;

      // Handle special angle cases
      if (Math.abs(Math.cos(angle)) < 0.001) {
        // Vertical direction (90° or 270°)
        return {
          x: fromCenterX,
          y: fromCenterY + Math.sign(dy) * fromHalfH
        };
      }

      if (Math.abs(Math.sin(angle)) < 0.001) {
        // Horizontal direction (0° or 180°)
        return {
          x: fromCenterX + Math.sign(dx) * fromHalfW,
          y: fromCenterY
        };
      }

      // Calculate intersection with rectangle edges
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      const absCos = Math.abs(cos);
      const absSin = Math.abs(sin);

      let edgeX, edgeY;

      // Determine which edge the line intersects
      if (absCos * fromHalfH <= absSin * fromHalfW) {
        // Intersects with top or bottom edge
        edgeX = fromCenterX + (fromHalfH / absSin) * cos * Math.sign(sin);
        edgeY = fromCenterY + fromHalfH * Math.sign(sin);

        // Check if the intersection point is within the width of the rectangle
        if (Math.abs(edgeX - fromCenterX) > fromHalfW) {
          // If not, it must intersect with the side instead
          edgeX = fromCenterX + fromHalfW * Math.sign(cos);
          edgeY = fromCenterY + (fromHalfW / absCos) * sin * Math.sign(cos);
        }
      } else {
        // Intersects with left or right edge
        edgeX = fromCenterX + fromHalfW * Math.sign(cos);
        edgeY = fromCenterY + (fromHalfW / absCos) * sin * Math.sign(cos);

        // Check if the intersection point is within the height of the rectangle
        if (Math.abs(edgeY - fromCenterY) > fromHalfH) {
          // If not, it must intersect with the top/bottom instead
          edgeX = fromCenterX + (fromHalfH / absSin) * cos * Math.sign(sin);
          edgeY = fromCenterY + fromHalfH * Math.sign(sin);
        }
      }

      return { x: edgeX, y: edgeY };
    }
  }, []);

  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    if (canvas.width !== rect.width * dpr || canvas.height !== rect.height * dpr) {
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.translate(viewOffset.x, viewOffset.y);
    ctx.scale(scale, scale);

    ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)';
    ctx.lineWidth = 0.5 / scale;
    const gridSize = 50;
    const startX = Math.floor(-viewOffset.x / scale / gridSize) * gridSize;
    const startY = Math.floor(-viewOffset.y / scale / gridSize) * gridSize;
    const endX = startX + (canvas.width / scale / dpr) + gridSize;
    const endY = startY + (canvas.height / scale / dpr) + gridSize;

    for (let x = startX; x <= endX; x += gridSize) {
      ctx.beginPath(); ctx.moveTo(x, startY); ctx.lineTo(x, endY); ctx.stroke();
    }
    for (let y = startY; y <= endY; y += gridSize) {
      ctx.beginPath(); ctx.moveTo(startX, y); ctx.lineTo(endX, y); ctx.stroke();
    }

    // Draw connections with flowing arrow effects
    elements.forEach(element => {
      element.connections.forEach(connectionId => {
        const target = elements.find(e => e.id === connectionId);
        if (!target) return;

        // 计算从源元素到目标元素的边缘点
        const startPoint = getEdgeOfElement(element, target);
        // 计算从目标元素到源元素的边缘点
        const endPoint = getEdgeOfElement(target, element);

        // 检查坐标是否有效（非 NaN 和非无穷大）
        const isValidCoord = (n: number) => isFinite(n) && !isNaN(n);
        const validCoords = isValidCoord(startPoint.x) && isValidCoord(startPoint.y) &&
          isValidCoord(endPoint.x) && isValidCoord(endPoint.y);

        if (!validCoords) return;

        ctx.beginPath();
        ctx.moveTo(startPoint.x, startPoint.y);
        ctx.lineTo(endPoint.x, endPoint.y);

        // 为所有连线添加流动效果
        const gradient = ctx.createLinearGradient(startPoint.x, startPoint.y, endPoint.x, endPoint.y);
        const phase = (Date.now() * 0.002) % 1;
        gradient.addColorStop(0, 'rgba(76, 201, 240, 0.3)');
        gradient.addColorStop(phase, 'rgba(76, 201, 240, 1)');
        gradient.addColorStop(Math.min(1, phase + 0.3), 'rgba(76, 201, 240, 0.3)');
        ctx.strokeStyle = gradient;
        
        ctx.lineWidth = 2.5 / scale;
        ctx.setLineDash([8 / scale, 4 / scale]);
        ctx.lineDashOffset = -animationFrame * 0.5;
        ctx.stroke();
        ctx.setLineDash([]);

        // 绘制箭头
        const angle = Math.atan2(endPoint.y - startPoint.y, endPoint.x - startPoint.x);
        const arrowLength = 12 / scale;
        ctx.fillStyle = 'rgba(76, 201, 240, 1)';
        ctx.beginPath();
        ctx.moveTo(endPoint.x, endPoint.y);
        ctx.lineTo(endPoint.x - arrowLength * Math.cos(angle - Math.PI / 6), endPoint.y - arrowLength * Math.sin(angle - Math.PI / 6));
        ctx.lineTo(endPoint.x - arrowLength * Math.cos(angle + Math.PI / 6), endPoint.y - arrowLength * Math.sin(angle + Math.PI / 6));
        ctx.closePath();
        ctx.fill();
      });
    });

    elements.forEach(element => {
      switch (element.type) {
        case 'character': drawCharacterBadge(ctx, element); break;
        case 'scene': drawSceneCard(ctx, element); break;
        case 'event': drawEventNode(ctx, element); break;
        case 'worldbuilding': drawWorldbuildingElement(ctx, element); break;
        // 移除聊天框绘制功能
      }
    });

    if (selectionBox && isSelecting) {
      ctx.strokeStyle = 'rgba(67, 97, 238, 0.8)';
      ctx.fillStyle = 'rgba(67, 97, 238, 0.1)';
      ctx.lineWidth = 1 / scale;
      const { x, y, width, height } = { x: Math.min(selectionBox.start.x, selectionBox.end.x), y: Math.min(selectionBox.start.y, selectionBox.end.y), width: Math.abs(selectionBox.start.x - selectionBox.end.x), height: Math.abs(selectionBox.start.y - selectionBox.end.y) };
      ctx.fillRect(x, y, width, height);
      ctx.strokeRect(x, y, width, height);
    }

    ctx.restore();
  }, [elements, viewOffset, scale, selectionBox, isSelecting,
    drawCharacterBadge, drawSceneCard, drawEventNode, drawWorldbuildingElement, animationFrame, getEdgeOfElement]);

  const handleAddElement = useCallback((type: 'character' | 'scene' | 'event' | 'worldbuilding') => {
    if (!onElementAdd || isReadOnly) return;
    const sizes = { character: { w: 80, h: 80 }, scene: { w: 220, h: 140 }, event: { w: 120, h: 80 }, worldbuilding: { w: 120, h: 120 } };
    const colors = { character: '#4361ee', scene: '#b21f1f', event: '#f72585', worldbuilding: '#3a6186' };
    onElementAdd({
      type, x: contextMenuWorldPos.x, y: contextMenuWorldPos.y,
      width: sizes[type].w, height: sizes[type].h,
      title: t(`canvas.defaultNames.${type}`), content: t(`canvas.defaultContent.${type}`),
      color: colors[type], connections: [], attributes: {}
    });
    setShowContextMenu(false);
  }, [contextMenuWorldPos, onElementAdd, isReadOnly, t]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const resizeObserver = new ResizeObserver(() => drawCanvas());
    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [drawCanvas]);

  useEffect(() => {
    elements.forEach(el => {
      if (el.type === 'character' && el.imageUrl && !imageCache[el.imageUrl]) {
        const img = new Image();
        img.crossOrigin = "Anonymous";
        img.src = el.imageUrl;
        img.onload = () => {
          setImageCache(prev => ({ ...prev, [el.imageUrl!]: img }));
        };
      }
    });
  }, [elements, imageCache]);

  useEffect(() => {
    let animId: number;
    const animate = () => {
      setAnimationFrame(prev => prev + 1);
      animId = requestAnimationFrame(animate);
    };
    animate();
    return () => cancelAnimationFrame(animId);
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    const worldPos = screenToWorld(e.clientX, e.clientY);
    const element = getElementAt(worldPos.x, worldPos.y);
    const currentTime = Date.now();

    if (e.button === 2) {
      e.preventDefault();
      if (!isReadOnly) {
        setContextMenuPos({ x: e.clientX, y: e.clientY });
        setContextMenuWorldPos(worldPos);
        setContextMenuElement(element);
        setShowContextMenu(true);
        if (element) onElementRightClick?.(element, e.clientX, e.clientY);
        else onCanvasRightClick?.(e.clientX, e.clientY);
      }
      return;
    }

    setShowContextMenu(false);

    // Handle double-click detection
    if (element && !isReadOnly) {
      const isDoubleClick =
        currentTime - lastClickTime < 300 &&
        lastClickElement === element.id;

      if (isDoubleClick) {
        // Double-click detected - open edit dialog
        onElementEditStart?.(element);
        setLastClickTime(0);
        setLastClickElement(null);
        return;
      } else {
        // Single click - update tracking
        setLastClickTime(currentTime);
        setLastClickElement(element.id);
      }
    }

    if (element) {
      const handle = getResizeHandle(element, worldPos.x, worldPos.y);
      if (handle && !isReadOnly && selectedElements.includes(element.id)) {
        setIsResizing(true);
        setResizeHandle(handle);
        setDraggedElement(element.id);
        setResizeStartSize({ width: element.width, height: element.height });
        setDragStart({ x: worldPos.x, y: worldPos.y });
        return;
      }

      if (e.ctrlKey || e.metaKey) {
        const newSelection = selectedElements.includes(element.id) ? selectedElements.filter(id => id !== element.id) : [...selectedElements, element.id];
        setSelectedElements(newSelection);
        onElementsSelect?.(newSelection);
      } else if (!selectedElements.includes(element.id)) {
        setSelectedElements([element.id]);
        onElementsSelect?.([element.id]);
      }

      if (!isReadOnly) {
        setDraggedElement(element.id);
        setDragStart({ x: worldPos.x - element.x, y: worldPos.y - element.y });
      }
    } else {
      if (e.shiftKey) {
        setIsSelecting(true);
        setSelectionBox({ start: worldPos, end: worldPos });
      } else {
        setIsDragging(true);
        setDragStart({ x: e.clientX - viewOffset.x, y: e.clientY - viewOffset.y });
        setSelectedElements([]);
        onElementsSelect?.([]);
      }
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    const worldPos = screenToWorld(e.clientX, e.clientY);
    const canvas = canvasRef.current;
    if (!canvas) return;

    let newCursor = 'grab';

    if (isDragging) {
      setViewOffset({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
      newCursor = 'grabbing';
    } else if (isSelecting && selectionBox) {
      setSelectionBox(prev => prev ? { ...prev, end: worldPos } : null);
      newCursor = 'crosshair';
    } else if (isResizing && draggedElement && resizeHandle && onElementUpdate) {
      const element = elements.find(el => el.id === draggedElement);
      if (element) {
        const deltaX = worldPos.x - dragStart.x;
        const deltaY = worldPos.y - dragStart.y;
        let newWidth = resizeStartSize.width, newHeight = resizeStartSize.height;
        let newX = element.x, newY = element.y;

        if (resizeHandle.includes('e')) newWidth = Math.max(40, resizeStartSize.width + deltaX);
        if (resizeHandle.includes('w')) {
          newWidth = Math.max(40, resizeStartSize.width - deltaX);
          newX = element.x + (resizeStartSize.width - newWidth);
        }
        if (resizeHandle.includes('s')) newHeight = Math.max(40, resizeStartSize.height + deltaY);
        if (resizeHandle.includes('n')) {
          newHeight = Math.max(40, resizeStartSize.height - deltaY);
          newY = element.y + (resizeStartSize.height - newHeight);
        }
        onElementUpdate(draggedElement, { x: newX, y: newY, width: newWidth, height: newHeight });
        newCursor = `${resizeHandle}-resize`;
      }
    } else if (draggedElement && onElementUpdate && !isResizing) {
      const newX = worldPos.x - dragStart.x;
      const newY = worldPos.y - dragStart.y;
      if (selectedElements.length > 1 && selectedElements.includes(draggedElement)) {
        const draggedEl = elements.find(el => el.id === draggedElement);
        if (draggedEl) {
          const deltaX = newX - draggedEl.x, deltaY = newY - draggedEl.y;
          selectedElements.forEach(id => {
            const el = elements.find(e => e.id === id);
            if (el) onElementUpdate(id, { x: el.x + deltaX, y: el.y + deltaY });
          });
        }
      } else {
        onElementUpdate(draggedElement, { x: newX, y: newY });
      }
      newCursor = 'grabbing';
    } else {
      const el = getElementAt(worldPos.x, worldPos.y);
      if (el && selectedElements.includes(el.id)) {
        const handle = getResizeHandle(el, worldPos.x, worldPos.y);
        if (handle) {
          newCursor = `${handle}-resize`;
        } else {
          newCursor = 'move';
        }
      }
    }
    if (canvas.style.cursor !== newCursor) {
      canvas.style.cursor = newCursor;
    }
  };

  const handleMouseUp = () => {
    if (isSelecting && selectionBox) {
      const elementsInBox = elements.filter(el => {
        const minX = Math.min(selectionBox.start.x, selectionBox.end.x);
        const maxX = Math.max(selectionBox.start.x, selectionBox.end.x);
        const minY = Math.min(selectionBox.start.y, selectionBox.end.y);
        const maxY = Math.max(selectionBox.start.y, selectionBox.end.y);
        return el.x < maxX && el.x + el.width > minX && el.y < maxY && el.y + el.height > minY;
      });
      const newSelection = elementsInBox.map(el => el.id);
      setSelectedElements(newSelection);
      onElementsSelect?.(newSelection);
      setSelectionBox(null);
      setIsSelecting(false);
    }
    setIsDragging(false);
    setDraggedElement(null);
    setIsResizing(false);
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    const newScale = Math.max(0.1, Math.min(5, scale * delta));
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    setViewOffset(prev => ({
      x: mouseX - (mouseX - prev.x) * (newScale / scale),
      y: mouseY - (mouseY - prev.y) * (newScale / scale)
    }));
    setScale(newScale);
  };

  const handleFitToScreen = () => {
    if (elements.length === 0) {
      setScale(1);
      setViewOffset({ x: 0, y: 0 });
      return;
    }

    const dpr = window.devicePixelRatio || 1;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const canvasWidth = canvas.width / dpr;
    const canvasHeight = canvas.height / dpr;

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    elements.forEach(el => {
      minX = Math.min(minX, el.x);
      minY = Math.min(minY, el.y);
      maxX = Math.max(maxX, el.x + el.width);
      maxY = Math.max(maxY, el.y + el.height);
    });

    const contentWidth = maxX - minX;
    const contentHeight = maxY - minY;
    const padding = 50;

    const newScale = Math.min((canvasWidth - padding * 2) / contentWidth, (canvasHeight - padding * 2) / contentHeight, 2);

    const newViewOffsetX = (canvasWidth - contentWidth * newScale) / 2 - minX * newScale;
    const newViewOffsetY = (canvasHeight - contentHeight * newScale) / 2 - minY * newScale;

    setScale(newScale);
    setViewOffset({ x: newViewOffsetX, y: newViewOffsetY });
  };



  return (
    <div
      ref={containerRef}
      className="infinite-canvas"
      style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden', background: 'linear-gradient(135deg, #1a2a6c 0%, #16213e 50%, #3a6186 100%)' }}
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Delete' && selectedElements.length > 0 && onElementDelete && !isReadOnly) { selectedElements.forEach(id => onElementDelete(id)); setSelectedElements([]); onElementsSelect?.([]); } }}
    >
      <canvas
        ref={canvasRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
        onContextMenu={(e) => e.preventDefault()}
        style={{ display: 'block', width: '100%', height: '100%' }}
      />

      {showContextMenu && !isReadOnly && (
        <div style={{ position: 'fixed', left: contextMenuPos.x, top: contextMenuPos.y, background: 'rgba(0, 0, 0, 0.9)', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: '8px', padding: '8px', zIndex: 1000, backdropFilter: 'blur(10px)', minWidth: '200px' }}>
          {contextMenuElement ? (
            <div>
              <div style={{ color: '#ffffff', fontSize: '14px', fontWeight: 'bold', marginBottom: '8px' }}>{contextMenuElement.title}</div>
              <button onClick={() => { onElementEditStart?.(contextMenuElement); setShowContextMenu(false); }} style={{ display: 'block', width: '100%', padding: '8px 12px', background: 'transparent', border: 'none', color: '#ffffff', textAlign: 'left', cursor: 'pointer', borderRadius: '4px' }}>✏️ {t('canvas.contextMenu.edit')}</button>
              <button onClick={() => { if (onElementDelete) onElementDelete(contextMenuElement.id); setShowContextMenu(false); }} style={{ display: 'block', width: '100%', padding: '8px 12px', background: 'transparent', border: 'none', color: '#ff6b6b', textAlign: 'left', cursor: 'pointer', borderRadius: '4px' }}>🗑️ {t('canvas.contextMenu.delete')}</button>
            </div>
          ) : (
            <div>
              <div style={{ color: '#ffffff', fontSize: '14px', fontWeight: 'bold', marginBottom: '8px' }}>{t('canvas.addElement')}</div>
              {(['character', 'scene', 'event', 'worldbuilding'] as const).map(type => (
                <button key={type} onClick={() => handleAddElement(type)} style={{ display: 'block', width: '100%', padding: '8px 12px', background: 'transparent', border: 'none', color: '#ffffff', textAlign: 'left', cursor: 'pointer', borderRadius: '4px' }}>
                  {type === 'character' ? '👤' : type === 'scene' ? '🎬' : type === 'event' ? '⚡' : '🌍'} {t(`canvas.elementTypes.${type}`)}
                </button>
              ))}
            </div>
          )}
        </div>
      )}



      {/* 右侧缩放控制面板 */}
      <div style={{ position: 'absolute', top: '16px', right: '16px', background: 'rgba(0,0,0,0.7)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', padding: '4px', display: 'flex', flexDirection: 'column', gap: '4px', backdropFilter: 'blur(10px)' }}>
        <button title="Zoom In" onClick={() => setScale(s => Math.min(5, s * 1.2))} style={{ background: 'transparent', border: 'none', color: '#fff', padding: '8px', cursor: 'pointer' }}>➕</button>
        <button title="Zoom Out" onClick={() => setScale(s => Math.max(0.1, s * 0.8))} style={{ background: 'transparent', border: 'none', color: '#fff', padding: '8px', cursor: 'pointer' }}>➖</button>
        <button title="Reset View" onClick={() => { setScale(1); setViewOffset({ x: 0, y: 0 }); }} style={{ background: 'transparent', border: 'none', color: '#fff', padding: '8px', cursor: 'pointer' }}>⌂</button>
        <button title="Fit to Screen" onClick={handleFitToScreen} style={{ background: 'transparent', border: 'none', color: '#fff', padding: '8px', cursor: 'pointer' }}>⛶</button>
        <div style={{ height: '1px', background: 'rgba(255,255,255,0.2)', margin: '4px 0' }}></div>
        <div style={{ padding: '8px', color: '#fff', fontSize: '12px', textAlign: 'center' }}>{Math.round(scale * 100)}%</div>
        <div style={{ padding: '0 8px 8px', color: 'rgba(255,255,255,0.7)', fontSize: '12px', textAlign: 'center' }}>{t('canvas.canvasInfo.elements')}: {elements.length}</div>
      </div>
    </div>
  );
};

export default InfiniteCanvas;